import { CONTENT_VIEW_PREFERENCE_KEY } from '@/common/constants';
import { useAppSelector } from '@/hooks/store';
import { selectCurrentAccount } from '@/store/features/account/current-account.slice';
import { selectCurrentUser } from '@/store/features/users/current-user.slice';
import { ContentViewMode } from '@/utils/helpers';
import { createUserScopedStorage } from '@/utils/localStorage';
import { useMemo, useState } from 'react';

type ContentViewModeOptions = {
  canUseBrowseView: boolean;
  isBrowseViewDefault: boolean;
};

export const useContentViewMode = ({
  canUseBrowseView,
  isBrowseViewDefault,
}: ContentViewModeOptions) => {
  const account = useAppSelector(selectCurrentAccount);
  const currentUser = useAppSelector(selectCurrentUser);

  const storageKey = `${account?.id}-${currentUser?.id}`;
  const storage = useMemo(
    () =>
      createUserScopedStorage<ContentViewMode>(CONTENT_VIEW_PREFERENCE_KEY)(
        storageKey,
      ),
    [storageKey],
  );

  const [contentViewMode, setContentViewMode] = useState<ContentViewMode>(
    () => {
      const storedViewMode = storage.get();

      if (
        storedViewMode &&
        Object.values(ContentViewMode).includes(storedViewMode)
      ) {
        return storedViewMode;
      }

      return isBrowseViewDefault
        ? ContentViewMode.BROWSE
        : ContentViewMode.MANAGE;
    },
  );

  const handleContentViewChange = (viewMode: ContentViewMode) => {
    setContentViewMode(viewMode);
    storage.set(viewMode);
  };

  return {
    contentViewMode,
    handleContentViewChange,
    isBrowseView:
      canUseBrowseView && contentViewMode === ContentViewMode.BROWSE,
  };
};
